/* global SideDrawer, debounce, initLazyImages */

if (!customElements.get('extend-facets-filters')) {
  class FetchProductCollection extends SideDrawer {

    /**
     * Add the event listener when the component is connected
     * @returns {void}
     */
    connectedCallback() {
      this.initSettings();
      this.init();
    }

    /**
     * Remove the event listener when the component is disconnected
     * @returns {void}
     */
    disconnectedCallback() {
      window.removeEventListener('productCollectionFetched', this.productCollection);
    }

    /**
     * Initialize the settings
     * @returns {void}
     */
    initSettings() {
      this.storefrontAccessToken = theme?.settings?.themeStoreId;
      this.shopifyGraphQlApiVersion = theme?.settings?.themeApiVersion;
      this.shopifyGraphQlEndpoint = `https://${this.dataset.endpoint}/api/${this.shopifyGraphQlApiVersion}/graphql.json`;

      this.requestMetafieldsList = [
        { namespace: "app", key: "product_free_shipping" },
        { namespace: "app", key: "product_spec_table" },
        { namespace: "app", key: "product_details" },
        { namespace: "custom", key: "rs_qs_inventory" },
        { namespace: "custom", key: "rep_model" },
        { namespace: "bestseller", key: "product_ranking" }
      ];

      this.requestVariantsMetafieldsList = [
        { namespace: "shipping", key: "free_shipping" },
        { namespace: "shipping", key: "quick_ship" }
      ];

      // Enable debug mode
      this.enabledDebug = false;
    }

    /**
     * Initialize the component
     * @returns {void}
     */
    init() {
      this.preloader = document.querySelector('.extended-facets__filters');
      this.form = document.getElementById('facets');
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.resultsCount = parseInt(this.dataset.results_count);
      this.collectionHandle = this.dataset.collection;
      this.typePage = this.dataset.type_page;
      this.endpoint = this.dataset.endpoint;
      this.terms = this.dataset.terms;
      this.types = this.dataset.types;
      this.vendor_search = this.dataset.vendor_search;
      this.sortKey = '';
      this.sortReverse = '';
      this.productCollection = {};
      this.categoryProductsQueryParams = '';
      this.quickShipMetafildFilter = '';

      let searchParams = this.getSearchParams();
      let filters = this.getRequestParams(searchParams);

      const hasProductVendorFilter = filters.some(filter => 'productVendor' in filter);
      const hasProductTypeFilter = filters.some(filter => 'productType' in filter);
      this.showMoreFilterBlock(hasProductTypeFilter);

      // Hide the vendor filter block if productType filter was applied
      let vendorFilterBlock = document.getElementById('filter-filter.p.vendor');
      if (hasProductTypeFilter && !hasProductVendorFilter) {
        if (vendorFilterBlock) {
         // vendorFilterBlock.classList.add('hidden'); //disabled per client request
        }
      }

      // Hide the vendor filter block if is Vendor list page
      if (this.dataset.collection === 'vendors') {
        vendorFilterBlock.classList.add('hidden');
      }

      // Fetch products if productType filter was applied
      if (hasProductTypeFilter && this.resultsCount > 1) {
        this.getProductCollection(filters);
      } else {
        this.productCollection = {};
        this.removeEventListener('productCollectionFetched', this.productCollection);
      }
    }

    /**
     * Show or hide the filter message block
     * @param flag
     */
    showMoreFilterBlock(flag) {
      const filterMessageBlock = document.getElementById('facet-message-block');
      if (filterMessageBlock) {
        filterMessageBlock.classList.toggle('hidden', flag);
      }
    }

    /**
     * Get the search parameters from the URL
     * @returns {string}
     */
    getSearchParams() {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);

      if (this.sortingEnabled) {
        const currentSortBy = searchParams.get('sort_by');
        searchParams.set('sort_by', currentSortBy);
      }

      // Remove empty parameters
      for (const key of [...searchParams.keys()]) {
        if (!searchParams.get(key)) {
          searchParams.delete(key);
        }
      }

      return searchParams.toString();
    }

    /**
     * Get the request parameters from the search parameters passed in the URL
     * @param searchParams
     * @returns {*[]}
     */
    getRequestParams(searchParams) {
      const filters = [];
      const queryParams = [];

      const paramObj = searchParams.split('&').reduce((acc, pair) => {
        const [key, value] = pair.split('=');
        acc[key] = acc[key] || [];
        acc[key].push(decodeURIComponent(value));
        return acc;
      }, {});

      for (const [key, values] of Object.entries(paramObj)) {
        if (key.startsWith('sort_by')) {
          this.processSortKey(values[0]);
        }

        if (key.startsWith('filter.p.m.')) {
          const [, , , productMetafieldNamespace, productMetafieldKey] = key.split('.');
          values.forEach(value => {
            filters.push({
              "productMetafield": {
                "namespace": productMetafieldNamespace,
                "key": productMetafieldKey,
                "value": value === "1" ? "true" : value
              }
            });
          });
        }

        if (key.startsWith('filter.v.m.')) {
          const [, , , variantMetafieldNamespace, variantMetafieldKey] = key.split('.');
          values.forEach(value => {
            filters.push({
              "variantMetafield": {
                "namespace": variantMetafieldNamespace,
                "key": variantMetafieldKey,
                "value": value === "1" ? "true" : value
              }
            });

            this.quickShipMetafildFilter = variantMetafieldKey === 'quick_ship' && value === "1";
          });
        }

        if (key.startsWith('filter.p.vendor')) {
          values.forEach(value => filters.push({ "productVendor": value.replace(/\+/g, ' ') }));
        }

        if (key.startsWith('filter.p.product_type')) {
          values.forEach(value => filters.push({ "productType": value.replace(/\+/g, ' ') }));
        }

        if (this.vendor_search !== null) {
          if (key === 'filter.p.product_type') {
            values.forEach(value => queryParams.push(`product_type:[${value.replace(/\+/g, ' ')}]`));
          }

          if (key === 'q') {
            values.forEach(value => queryParams.push(`vendor:[${value.replace(/\+/g, ' ')}]`));
          }
        }
      }

      if (this.vendor_search !== null) {
        this.categoryProductsQueryParams = queryParams.join(', ').toString();
      }

      return filters;
    }

    /**
     * Process the sort key from the search parameters
     * @param value
     */
    processSortKey(value) {
      const sortOptions = {
        "null": { key: "MANUAL", reverse: false },
        "manual": { key: "MANUAL", reverse: false },
        "best-selling": { key: "BEST_SELLING", reverse: false },
        "title-ascending": { key: "TITLE", reverse: false },
        "title-descending": { key: "TITLE", reverse: true },
        "price-ascending": { key: "PRICE", reverse: false },
        "price-descending": { key: "PRICE", reverse: true },
        "created-ascending": { key: "CREATED", reverse: false },
        "created-descending": { key: "CREATED", reverse: true },
        "updated-ascending": { key: "UPDATED", reverse: false },
        "updated-descending": { key: "UPDATED", reverse: true },
        "collection-default": { key: "RELEVANCE", reverse: false },
        "relevance": { key: "RELEVANCE", reverse: false }
      };

      const option = sortOptions[value];
      if (option) {
        this.sortKey = option.key;
        this.sortReverse = option.reverse;
      }
    }

    /**
     * Fetch products for a collection or search result
     * @param filters
     * @returns {Promise<void>}
     */
    async getProductCollection(filters) {
      this.preloader.classList.add('is-loading');
      await this.fetchProducts(filters, this.requestMetafieldsList, this.requestVariantsMetafieldsList);
      this.preloader.classList.remove('is-loading');
    }

    /**
     * Fetch products using the GraphQL API
     * @param filters
     * @param metafields
     * @param variantsMetafields
     * @param endCursor
     * @returns {Promise<void>}
     */
    async fetchProducts(filters, metafields, variantsMetafields, endCursor = null) {
      let variables = {};
      let query = '';
      this.products = [];

      if (this.typePage === 'category') {
        query = this.collectionHandle === 'vendors' ? this.categoryProductsQuery() : this.categoryCollectionQuery();
        variables = {
          handle: this.collectionHandle,
          filters: filters,
          metafields: metafields,
          variantsMetafields: variantsMetafields,
          after: endCursor
        };

        if (this.collectionHandle === 'vendors') {
          variables.query = this.categoryProductsQueryParams;
        }
      }

      if (this.typePage === 'search') {
        query = this.searchCollectionQuery();
        variables = {
          query: this.terms,
          filters: filters,
          after: endCursor,
          metafields: metafields,
          variantsMetafields: variantsMetafields
        };
      }

      variables.sortKey = this.sortKey;
      variables.reverse = this.sortReverse;

      try {
        let hasNextPage = true;
        while (hasNextPage) {
          const response = await fetch(this.shopifyGraphQlEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
            },
            body: JSON.stringify({ query, variables })
          });

          if (!response.ok) {
            throw new Error(`Network response incorrect: ${response.statusText}`);
          }

          const data = await response.json();
          if (!data?.data?.collection && !data.data.products && !data.data.search) {
            break;
          }

          const responseObject = this.typePage === 'category' && this.collectionHandle !== 'vendors'
              ? data.data.collection.products
              : data.data.products || data.data.search;

          let products = responseObject.edges;
          this.prepareResponseData(products);
          this.products = this.products.concat(products);

          // Handle pagination
          hasNextPage = this.getPageInfo(this.typePage, data);
          if (hasNextPage) {
            variables.after = this.getEndCursor(this.typePage, data);
          }

          // Dispatch event with the current batch (250 or less)
          this.dispatchEvent(new CustomEvent('productCollectionFetched', {
            detail: {
              productCollection: this.productCollection,
              message: 'fetched_batch_products'
            },
            bubbles: true
          }));
        }

        if (this.products && this.products.length > 250) {
          // Final dispatch with all products collected
          this.dispatchEvent(new CustomEvent('productCollectionFetched', {
            detail: {
              productCollection: this.prepareResponseData(this.products),
              message: 'fetched_all_products'
            },
            bubbles: true
          }));
        }

      } catch (error) {
        console.error('Error fetching products:', error);
      }
    }

    /**
     * Prepare the response data for the product collection
     * @param products
     * @returns {*|{}|{}|{totalResults, handle: *, type: *, products}}
     */
    prepareResponseData(products) {

      if (this.collectionHandle === 'vendors' && products) {
        // Apply quick_ship filter if specified
        if (this.quickShipMetafildFilter) {
          products = this.filterProductsByQuickShip(products);
        }
      }

      // Prepare productCollection
      this.productCollection = {
        type: this.typePage,
        handle: this.collectionHandle,
        products: products,
        totalResults: products.length
      };

      if (this.typePage === 'search') {
        this.productCollection.query = this.terms;
      }

      return this.productCollection;
    }

    /**
     * Filter products by quick ship
     * @param products
     * @returns {*}
     */
    filterProductsByQuickShip(products) {
      return products.filter(product => {
        const quickShipMetafield = product.node.metafields.find(metafield => metafield && metafield.key === 'quick_ship');
        return quickShipMetafield ? quickShipMetafield.value === this.quickShipMetafildFilter.toString() : false;
      });
    }

    /**
     * Check if there's a next page based on the type of page and data returned
     * @param {string} typePage
     * @param {Object} data
     * @returns {boolean}
     */
    getPageInfo(typePage, data) {
      if (typePage === 'category') {
        return data.data.collection ? data.data.collection.products.pageInfo.hasNextPage : false;
      }
      if (typePage === 'search') {
        return data.data.search.pageInfo.hasNextPage;
      }
      return false;
    }

    /**
     * Get the end cursor for pagination based on the type of page and data returned
     * @param {string} typePage
     * @param {Object} data
     * @returns {string|null}
     */
    getEndCursor(typePage, data) {
      if (typePage === 'category') {
        return data.data.collection ? data.data.collection.products.pageInfo.endCursor : null;
      }
      if (typePage === 'search') {
        return data.data.search.pageInfo.endCursor;
      }
      return null;
    }


    /**
     * GraphQL query to fetch products for a collection
     * @returns {string}
     */
    categoryCollectionQuery() {
      return `
        query categoryCollection(
            $handle: String!,
            $filters: [ProductFilter!],
            $after: String,
            $metafields: [HasMetafieldsIdentifier!]!,
            $variantsMetafields :[HasMetafieldsIdentifier!]!,
            $sortKey: ProductCollectionSortKeys!,
            $reverse: Boolean
        ) {
          collection(handle: $handle) {
            handle
            id
            title
            updatedAt
            products(first: 250, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
              edges {
                node {
                  id
                  title
                  handle
                  vendor
                  availableForSale
                  productType
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                  tags
                  variants(first: 10) {
                    edges {
                      node {
                        id
                        sku
                        availableForSale
                        weight
                        price {
                          amount
                          currencyCode
                        }
                        compareAtPrice {
                          amount
                          currencyCode
                        }
                        metafields(identifiers: $variantsMetafields) {
                          id
                          value
                          type
                          key
                        }
                      }
                    }
                  }
                  metafields(identifiers: $metafields) {
                    id
                    value
                    key
                    type
                    reference {
                      ... on Product {
                        id
                        handle
                        vendor
                      }
                    }
                  }
                }
              }
              pageInfo {
                endCursor
                hasNextPage
              }
            }
          }
        }
      `;
    }

    /**
     * GraphQL query to fetch products for a category
     * @returns {string}
     */
    categoryProductsQuery() {
      return `
        query categoryProducts (
          $after: String, 
          $metafields: [HasMetafieldsIdentifier!]!, 
          $variantsMetafields: [HasMetafieldsIdentifier!]!, 
          $reverse: Boolean, 
          $sortKey: ProductSortKeys!, 
          $query: String!
        ) {
        products(
            first: 250
            after: $after
            reverse: $reverse
            sortKey: $sortKey
            query: $query
          ) {
            edges {
              node {
                id
                title
                handle
                vendor
                availableForSale
                productType
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                compareAtPriceRange {
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
                tags
                variants(first: 10) {
                  edges {
                    node {
                      id
                      sku
                      availableForSale
                      weight
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      metafields(identifiers: $variantsMetafields) {
                        id
                        value
                        type
                        key
                      }
                    }
                  }
                }
                metafields(identifiers: $metafields) {
                  id
                  value
                  key
                  type
                  reference {
                    ... on Product {
                      id
                      handle
                      vendor
                    }
                  }
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      `;
    }

    /**
     * GraphQL query to fetch products for a search result
     * @returns {string}
     */
    searchCollectionQuery() {
      return `
        query searchCollection(
            $query: String!,
            $filters: [ProductFilter!],
            $metafields: [HasMetafieldsIdentifier!]!,
            $variantsMetafields: [HasMetafieldsIdentifier!]!, 
            $sortKey: SearchSortKeys!,
            $reverse: Boolean,
            $after: String
        ) {
          search(
            query: $query,
            types: PRODUCT,
            unavailableProducts: SHOW,
            first: 250,
            productFilters: $filters,
            after: $after,
            sortKey: $sortKey,
            reverse: $reverse
          ) {
            totalCount
            edges {
              node {
                ... on Product {
                  id
                  title
                  handle
                  vendor
                  availableForSale
                  productType
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                  metafields(identifiers: $metafields) {
                    id
                    value
                    key
                    type
                    reference {
                      ... on Product {
                        id
                        handle
                        vendor
                      }
                    }
                  }
                  tags
                  variants(first: 10) {
                    edges {
                      node {
                        id
                        sku
                        availableForSale
                        weight
                        price {
                          amount
                          currencyCode
                        }
                        compareAtPrice {
                          amount
                          currencyCode
                        }
                        metafields(identifiers: $variantsMetafields) {
                          id
                          value
                          type
                          key
                        }
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      `;
    }
  }

  customElements.define('extend-facets-filters', FetchProductCollection);
}
